<template>
  <div class="fileview">
    <div class="row row--header">
      <div class="cell cell--file">{{ dropzoneStrings.your_filename }}</div>
      <div class="cell cell--description">{{ fileKind === 'receipts' ? dropzoneStrings.receipt_description : dropzoneStrings.document_kind}}</div>
      <div class="cell cell--currency" v-if="fileKind === 'receipts'">{{ dropzoneStrings.receipt_currency }}</div>
      <div class="cell cell--amount" v-if="fileKind === 'receipts'">{{ dropzoneStrings.receipt_amount }}</div>
      <div class="cell cell--country-currency" v-if="fileKind === 'receipts'">{{ dropzoneStrings.receipt_country_currency }}</div>
      <div class="cell cell--controls"></div>
    </div>
    <div class="file"
      v-for="(fileObject) in fileRow"
      :key="fileObject.Id">
      <div class="row">
        <div class="cell cell--file cell--mobile">{{ dropzoneStrings.your_filename }}</div>
        <div class="cell cell--file">{{ fileObject.FileName || fileObject.Name || fileObject.Description }}</div>
        <div class="cell cell--description cell--mobile">{{ fileKind === 'receipts' ? dropzoneStrings.receipt_description : dropzoneStrings.document_kind }}</div>
        <div class="cell cell--description">{{ descriptionRenderMethod(fileObject) }}</div>
        <div class="cell cell--currency cell--mobile" v-if="fileKind === 'receipts'">{{ dropzoneStrings.receipt_currency }}</div>
        <div class="cell cell--currency" v-if="fileKind === 'receipts'">{{ fileObject.Currency }}</div>
        <div class="cell cell--amount cell--mobile" v-if="fileKind === 'receipts'">{{ dropzoneStrings.receipt_amount }}</div>
        <div class="cell cell--amount" v-if="fileKind === 'receipts'">{{ parseFloat(fileObject.Amount ? fileObject.Amount : '0').toFixed(2) }}</div>
        <div class="cell cell--amount cell--mobile" v-if="fileKind === 'receipts'">{{ dropzoneStrings.receipt_country_currency }}</div>
        <div class="cell cell--amount" v-if="fileKind === 'receipts'">{{ parseFloat(fileObject.AmountInEuros ? fileObject.AmountInEuros : '0').toFixed(2) }}</div>
        <div class="cell cell--controls">
          <a class="link link--edit link--view" @click="viewFile(fileObject.Id)">
            <LennocIcon image="eye" />
            <span>{{ dropzoneStrings.view_me }}</span>
          </a>
          <div class="event-host">
            <button
            :disabled="isDisabled(fileObject)"
            class="link link--edit link--action"
            @click="deleteFile(fileObject)"
            >
              <LennocIcon image="squared-cross" />
              <span>{{ dropzoneStrings.delete_me }}</span>
              <LennocToolTip v-if="isDisabled(fileObject)" :error="true">{{ dropzoneStrings.cant_delete_file }}</LennocToolTip>
            </button>
          </div>
          <LennocFileView
            v-if="viewerData === fileObject"
            :downloadedFile="downloadedFile"
            :popUpStrings="popUpStrings"
            :document="fileObject"
            @close="viewerData = null"
            :secret="secret"
            @download="emits('download', $event)" />
        </div>
        <LennocSpinner v-if="busy" />
        <LennocModal v-if="deleteData === fileObject">
          <p class="text">{{ dropzoneStrings.confirm_delete }}</p>
          <div class="button__group">
            <a
              data-button="sec"
              class="button button--sec" @click="confirmDeleteFile(fileObject)">{{ popUpStrings.buttons.confirm }}</a>
            <a
              data-button="sec"
              class="button button--sec" @click="deleteData = null">{{ popUpStrings.buttons.cancel }}</a>
          </div>
        </LennocModal>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { PropType, ref } from 'vue'
  import day from 'dayjs'

  import { FileObject } from '../../types/types'

  const props = defineProps({
    downloadedFile: {
      type: Object,
      default: {}
    },
    dropzoneStrings: {
      type: Object,
      required: true
    },
    fileRow: {
      type: Array as PropType<Array<FileObject>>,
      required: true
    },
    fileKind: {
      type: String,
      default: ''
    },
    popUpStrings: {
      type: Object,
      required: true
    },
    secret: {
      type: String,
      default () {
        ''
      }
    }
  })

  const emits = defineEmits([
    'deletedFile',
    'download'
  ])

  const busy = ref(false)
  const deleteData = ref()
  const viewerData = ref()

  const confirmDeleteFile = (fileObject : FileObject) => {
    busy.value = true
    deleteData.value = null
    emits('deletedFile', fileObject.Id || fileObject)
    busy.value = false
  }

  const deleteFile = (fileObject : FileObject) => {
    deleteData.value = fileObject
  }

  const descriptionRenderMethod = (fileObject : FileObject) => {
    return props.fileKind === 'receipts' || (!fileObject.kind && !fileObject.DocumentChecklist)
      ? renderDescriptions(fileObject.Description?.join(','))
      : renderFileTypes(fileObject.kind || fileObject.DocumentChecklist)
  }

  const isDisabled = (fileObject : FileObject) => {
    const disabledState = fileObject.TimeStamp
      ? day(fileObject.TimeStamp)
        .add(24, 'hour')
        .isBefore(day())
      : false
    return disabledState
  }

  const renderDescriptions = (descriptionsString : string | undefined) => {
    const keys = descriptionsString?.split(',')
    if (keys?.length === 1) {
      return props.dropzoneStrings.receipt_description_list[keys[0]]
    }
    const descriptions = []
    if (keys) {
      for (const d of keys) {
        descriptions.push(props.dropzoneStrings.receipt_description_list[d])
      }
      return descriptions.join(', ')
    }
  }

  const renderFileTypes = (fileKind : Record<string, any>) => {
    const types = []
    for (const t of Object.entries(fileKind)) {
      if (t[1]) {
        types.push(props.dropzoneStrings.file_types[t[0]])
      }
    }
    return types.join(', ')
  }

  const viewFile = (objectId : string | undefined) => {
    const fileToShow = props.fileRow.find(f => {
      return f.Id === objectId
    })
    viewerData.value = fileToShow
  }

</script>